import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { toast } from 'sonner';
import {
  Star, Loader2, Save, Plug, CheckCircle2, AlertTriangle, RefreshCw, Send,
  Trash2, Zap, Flame, Droplet, Link2, History, Wand2, PlusCircle, Eye, ShieldCheck,
  HelpCircle, Gauge, FlaskConical, Rocket, Ruler, Building2, PlugZap,
} from 'lucide-react';
import { ReadinessBadge, ScoreEligibilityBadge, ReadinessFilters } from '../energyStar/ReadinessUI';
import { matchFilter } from '../energyStar/readiness';

// Tooltip helper — explains what each action button does on hover/focus.
const Tip = ({ label, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent className="max-w-[240px]">{label}</TooltipContent>
  </Tooltip>
);

const COMMODITIES = [
  { key: 'electricity', label: 'Electricity', unit: 'kWh', icon: Zap },
  { key: 'gas', label: 'Natural Gas', unit: 'therms', icon: Flame },
  { key: 'water', label: 'Water', unit: 'gallons', icon: Droplet },
];

const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const FAQS = [
  { q: 'What should I do to get started and get it right?', a: 'Start in UBM Onboarding: make sure every property and all of its meters are onboarded there first. For ENERGY STAR to create the property and log data correctly, each property needs its address, gross floor area and primary function set (the primary function comes from the property’s type), and each relevant meter — electricity, gas and/or water — must be onboarded and linked to its property. You only onboard properties, meters, vendors and accounts in UBM Onboarding — usage (meter reads) flows in automatically from the bills as they’re parsed. Then come here, connect the Sandbox (Test) environment, review each property’s readiness, use Connect & sync and push a few months to confirm the numbers look right. Once you’re happy with the test, set it up the same way on Production.' },
  { q: 'How do I set up ESPM on the platform?', a: 'Open the tab for the environment you want (Sandbox first, then Production). Enter your ENERGY STAR® Portfolio Manager username and password, click Save, then Test Connection. Once it shows Connected, review the property list — each property shows whether it is Ready to connect or still needs information — then click Connect & sync.' },
  { q: 'What does the readiness status on each property mean?', a: 'It tells you at a glance whether a property is ready for ENERGY STAR. "Ready to connect" = it has an address, gross floor area, a primary function and at least one supported meter. "Missing property details" / "Missing meters" show exactly what to add. "Connected" = it is already created and mapped in ENERGY STAR. Use the filter pills (Ready / Needs information / Connected / Errors / All) to focus.' },
  { q: 'What is ENERGY STAR score eligibility?', a: 'More than 80 property types can be benchmarked in Portfolio Manager (they get Source/Site EUI and emissions), but only selected types receive the 1–100 ENERGY STAR score — e.g. Multifamily Housing, Office, Hotel, K-12 School. The badge on each property shows whether it is "1–100 score eligible", "Metrics only", or pending until you add the floor area/function.' },
  { q: 'Why must I set the gross floor area? Can’t you assume one?', a: 'No — ENERGY STAR metrics (EUI, score, emissions intensity) are all per square foot, so an assumed floor area would produce wrong numbers. If a property has no floor area we flag it as "Missing property details" rather than guess. Add the real GFA on the property in BillPortal and it becomes Ready to connect.' },
  { q: 'What are the "Property creation fallbacks"?', a: 'They are ONLY used to fill blanks when auto-creating a property in ESPM. If a property is missing its primary function or year built, the fallback value is used so creation does not fail. Floor area is never guessed — it must be set on the property itself. Each property still uses its OWN function/year/GFA whenever those exist.' },
  { q: 'What is the difference between Sandbox (Test) and Production?', a: 'They are two completely separate ENERGY STAR accounts with separate logins and separate data. Sandbox (portfoliomanager.energystar.gov/pmtest) is a safe playground; Production (portfoliomanager.energystar.gov/pm) holds your real, official records. Credentials are NOT shared between them, so each tab needs its own login.' },
  { q: 'What does "Connect & sync" do?', a: 'For a Ready property it creates the property + meters in ENERGY STAR (never duplicates) and immediately pushes the most recent 12 months of reads — enough for ENERGY STAR to start computing metrics and, for eligible types, a 1–100 score. Meter units of measure are set automatically to match your data (kWh, therms, US gallons).' },
  { q: 'How do I push meter reads manually?', a: 'In "Push meter reads", optionally set a date range, then click "Push now". Use "Preview (dry run)" first to see exactly what would be sent without sending anything. Months already pushed are skipped unless you turn on "Re-push existing months".' },
  { q: 'How do I push meter reads automatically?', a: 'Turn on the auto-push switch for the environment. On Production it runs on a daily schedule and sends any new, not-yet-pushed months every month without a miss. On Sandbox it sends a capped number of months and then stops (a safety cap for testing).' },
];

export default function EnergyStarTab({ currentAccount }) {
  const accountId = currentAccount?.id;

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [activeEnv, setActiveEnv] = useState('test');
  const activeEnvRef = useRef('test');
  useEffect(() => { activeEnvRef.current = activeEnv; }, [activeEnv]);
  const [initEnv, setInitEnv] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', auto_push: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [mapView, setMapView] = useState(null);
  const [loadingMap, setLoadingMap] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushRange, setPushRange] = useState({ date_from: '', date_to: '', estimated: false, force: false });
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [logs, setLogs] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [propDefaults, setPropDefaults] = useState({ primary_function: 'Other', year_built: 2000 });
  const [pfList, setPfList] = useState([]);
  const [provisioning, setProvisioning] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // site_uid currently connecting
  const [faqOpen, setFaqOpen] = useState(false);
  const [readinessFilter, setReadinessFilter] = useState('ready');

  const [mapDlg, setMapDlg] = useState(null);
  const [espmMeters, setEspmMeters] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState([]);
  const [selProp, setSelProp] = useState('');
  const [selMeter, setSelMeter] = useState('');
  const [dlgBusy, setDlgBusy] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/energy-star/config', { params: { account_id: accountId } });
      const c = data.config;
      setConfig(c);
      setPfList(c?.primary_functions || []);
      if (c?.property_defaults) setPropDefaults({
        primary_function: c.property_defaults.primary_function || 'Other',
        year_built: c.property_defaults.year_built || 2000,
      });
    } catch (e) {
      toast.error('Failed to load Energy Star settings');
    } finally { setLoading(false); }
  }, [accountId]);

  const refreshStatus = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await api.get('/energy-star/config', { params: { account_id: accountId } });
      setConfig(data.config);
    } catch (e) { /* non-critical */ }
  }, [accountId]);

  const loadMappings = useCallback(async () => {
    if (!accountId) return;
    const env = activeEnv;
    setLoadingMap(true);
    try {
      const to = new Date(); const from = new Date(); from.setFullYear(from.getFullYear() - 3);
      const iso = (d) => d.toISOString().slice(0, 10);
      const { data } = await api.get('/energy-star/mappings', { params: { account_id: accountId, environment: env, date_from: iso(from), date_to: iso(to) } });
      if (env === activeEnvRef.current) setMapView(data);   // ignore stale env response
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load properties');
    } finally { setLoadingMap(false); }
  }, [accountId, activeEnv]);

  const loadLogs = useCallback(async () => {
    if (!accountId) return;
    const env = activeEnv;
    try {
      const { data } = await api.get('/energy-star/push-history', { params: { account_id: accountId, environment: env, limit: 10 } });
      if (env === activeEnvRef.current) setLogs(data.logs || []);
    } catch (e) { /* non-critical */ }
  }, [accountId, activeEnv]);

  const loadKpi = useCallback(async () => {
    if (!accountId) return;
    const env = activeEnv;
    try {
      const { data } = await api.get('/energy-star/kpi', { params: { account_id: accountId, environment: env } });
      if (env === activeEnvRef.current) setKpi(data);
    } catch (e) { /* non-critical */ }
  }, [accountId, activeEnv]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => {
    if (config && !initEnv) { setActiveEnv(config.active_environment || 'test'); setInitEnv(true); }
  }, [config, initEnv]);
  useEffect(() => {
    const s = config?.environments?.[activeEnv];
    if (s) setForm({ username: s.username || '', password: '', auto_push: !!s.auto_push });
  }, [activeEnv, config]);
  useEffect(() => {
    if (!initEnv) return;
    loadMappings(); loadLogs(); loadKpi();
  }, [initEnv, loadMappings, loadLogs, loadKpi]);
  // Credentials accordion stays OPEN until connected; once connected it collapses
  // so the important set-and-track panels (Push, History) come forward.
  useEffect(() => {
    const isConn = !!config?.environments?.[activeEnv]?.connected;
    setSidebarOpen(isConn ? ['push', 'history'] : ['connection']);
  }, [config, activeEnv]);

  const envSlot = config?.environments?.[activeEnv] || {};
  const connected = !!envSlot.connected;
  const isTest = activeEnv === 'test';
  const limits = config?.limits?.test || { max_properties: 3, max_meters: 10, max_push_months: 2 };
  // First visit = no credentials configured or connected on EITHER environment.
  const firstTime = !['test', 'prod'].some((e) => {
    const s = config?.environments?.[e] || {};
    return s.connected || s.username || s.password_set;
  });

  const persistConfig = async ({ silent = false } = {}) => {
    const payload = { environment: activeEnv, auto_push: form.auto_push };
    payload.default_primary_function = propDefaults.primary_function;
    payload.default_year_built = Number(propDefaults.year_built) || undefined;
    payload.username = form.username;
    if (form.password) payload.password = form.password;
    const { data } = await api.put('/energy-star/config', payload, { params: { account_id: accountId } });
    setConfig(data.config);
    setForm((f) => ({ ...f, password: '' }));
    if (!silent) toast.success(`${isTest ? 'Sandbox' : 'Production'} connection saved`);
    return data.config;
  };

  const saveConfig = async () => {
    setSaving(true);
    try { await persistConfig(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const s = config?.environments?.[activeEnv] || {};
      const pending = !!form.password || form.username !== (s.username || '');
      if (pending) await persistConfig({ silent: true });
      const { data } = await api.post('/energy-star/test-connection', { environment: activeEnv }, { params: { account_id: accountId } });
      if (data.success) toast.success(`Connected to ESPM${data.account?.username ? ` as ${data.account.username}` : ''}`);
      else toast.error(data.message || 'Connection failed');
      await refreshStatus();
      await loadMappings();
      await loadKpi();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally { setTesting(false); }
  };

  const setRepresentation = async (site_uid, representation) => {
    try {
      await api.put('/energy-star/representation',
        { environment: activeEnv, site_uid, representation },
        { params: { account_id: accountId } });
      toast.success(representation === 'Whole Property'
        ? 'Set to Whole Property — meters re-associated'
        : 'Set to Common Area only — meters re-associated');
      loadMappings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update representation');
    }
  };

  const openMapDialog = async (p, commodity) => {
    setMapDlg({ site_uid: p.site_uid, property_name: p.name, commodity,
      espm_property_id: p.espm_property_id, mappings: p.mappings });
    setSelProp(p.espm_property_id || ''); setSelMeter(''); setEspmMeters(null);
    // The property is FIXED to the row that was clicked — only meters under that
    // property can be linked (no picking a different ESPM property here).
    if (!p.espm_property_id) return;  // not in ESPM yet — user must Connect & sync first
    setDlgBusy(true);
    try {
      const { data: md } = await api.get('/energy-star/espm/meters', { params: { account_id: accountId, environment: activeEnv, property_id: p.espm_property_id } });
      setEspmMeters(md.meters || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load meters');
      setEspmMeters([]);
    } finally { setDlgBusy(false); }
  };

  const saveMapping = async () => {
    if (!selProp || !selMeter) { toast.error('Pick an ESPM meter'); return; }
    const meter = (espmMeters || []).find((m) => m.id === selMeter);
    setDlgBusy(true);
    try {
      await api.put('/energy-star/mappings', {
        environment: activeEnv,
        site_uid: mapDlg.site_uid,
        property_name: mapDlg.property_name,
        commodity: mapDlg.commodity,
        espm_property_id: selProp,
        espm_meter_id: selMeter,
        espm_meter_name: meter?.name || mapDlg.property_name || '',
      }, { params: { account_id: accountId } });
      toast.success('Meter mapped');
      setMapDlg(null);
      await loadMappings(); await loadKpi();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save mapping');
    } finally { setDlgBusy(false); }
  };

  const removeMapping = async (mappingId) => {
    try {
      await api.delete(`/energy-star/mappings/${mappingId}`, { params: { account_id: accountId } });
      toast.success('Mapping removed');
      await loadMappings(); await loadKpi();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const autoCreate = async (siteUids, commodities, { push = false } = {}) => {
    const single = siteUids && siteUids.length === 1;
    if (single) setRowBusy(siteUids[0]); else setProvisioning(true);
    try {
      const body = { environment: activeEnv, push_after_create: push, push_months: 12 };
      if (siteUids) body.site_uids = siteUids;
      if (commodities) body.commodities = commodities;
      const { data } = await api.post('/energy-star/auto-create', body, { params: { account_id: accountId } });
      const { properties_created = 0, meters_created = 0, reused = 0, errors = 0, uses_created = 0, meters_associated = 0, capped, push: pushRes } = data;
      if (capped) toast.warning(data.cap_message || 'Sandbox limits reached.');
      else if (errors > 0) {
        const firstErr = (data.results || []).find((r) => r.status === 'error');
        toast.warning(`Created ${properties_created} propert(ies), ${meters_created} meter(s); ${reused} reused, ${errors} error(s)${firstErr ? ` — e.g. ${firstErr.message}` : ''}`);
      } else {
        const notes = [];
        if (uses_created > 0) notes.push(`${uses_created} property profile(s) set`);
        if (meters_associated > 0) notes.push(`${meters_associated} meter(s) linked to metrics`);
        if (pushRes) notes.push(`${fmt(pushRes.records_pushed)} month-read(s) pushed`);
        const suffix = notes.length ? ` · ${notes.join(' · ')}` : '';
        toast.success(`Created ${properties_created} propert(ies) + ${meters_created} meter(s) in ESPM (${reused} already existed)${suffix}`);
      }
      await loadMappings(); await loadKpi(); await loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connect failed');
    } finally { setProvisioning(false); setRowBusy(null); }
  };

  const previewPush = async () => {
    setPreviewing(true);
    try {
      const body = { environment: activeEnv, estimated: pushRange.estimated, force: pushRange.force, dry_run: true };
      if (pushRange.date_from) body.date_from = pushRange.date_from;
      if (pushRange.date_to) body.date_to = pushRange.date_to;
      const { data } = await api.post('/energy-star/push', body, { params: { account_id: accountId } });
      setPreview(data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const pushNow = async () => {
    setPushing(true);
    try {
      const body = { environment: activeEnv, estimated: pushRange.estimated, force: pushRange.force };
      if (pushRange.date_from) body.date_from = pushRange.date_from;
      if (pushRange.date_to) body.date_to = pushRange.date_to;
      const { data } = await api.post('/energy-star/push', body, { params: { account_id: accountId } });
      if (data.success) toast.success(`Pushed ${data.records_pushed} record(s) to ${data.meters} meter(s)`);
      else toast.warning(`Push completed with issues — ${data.records_pushed} record(s). See history.`);
      await loadLogs(); await loadMappings(); await loadKpi();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Push failed');
    } finally { setPushing(false); }
  };

  const allProps = mapView?.properties || [];
  const readinessCounts = mapView?.readiness_counts || {};
  const readyToConnect = useMemo(
    () => allProps.filter((p) => p.readiness?.status === 'ready').map((p) => p.site_uid),
    [allProps]);
  const visibleProps = useMemo(
    () => allProps.filter((p) => matchFilter(readinessFilter, p.readiness?.status)),
    [allProps, readinessFilter]);

  if (loading) {
    return <div className="flex items-center justify-center p-12" data-testid="energy-star-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const mappingCount = mapView?.mapping_count || 0;
  const counts = mapView?.counts || { properties: 0, meters: 0 };
  const kpiColor = kpi?.status === 'green' ? 'text-green-600' : kpi?.status === 'amber' ? 'text-amber-600' : 'text-red-600';
  const kpiBar = kpi?.status === 'green' ? 'bg-green-600' : kpi?.status === 'amber' ? 'bg-amber-500' : 'bg-red-600';

  const EnvTab = ({ env, icon: Icon, label }) => (
    <button
      onClick={() => setActiveEnv(env)}
      className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
        activeEnv === env ? 'border-amber-500 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
      data-testid={`espm-env-tab-${env}`}
    >
      <Icon className="h-4 w-4" />
      {label}
      {config?.environments?.[env]?.connected && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
    </button>
  );

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6" data-testid="energy-star-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Star className="h-6 w-6 text-amber-500" />
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Energy Star Portfolio Manager</h2>
            <p className="text-sm text-muted-foreground">Push monthly meter reads to ENERGY STAR® Portfolio Manager. Test in Sandbox, then go live on Production.</p>
          </div>
        </div>
        <Tip label="Open the setup guide and answers to common ESPM questions">
          <Button variant="outline" className="rounded-none" onClick={() => setFaqOpen(true)} data-testid="espm-faq-btn">
            <HelpCircle className="h-4 w-4 mr-2" /> Help / FAQ
          </Button>
        </Tip>
      </div>

      {/* Environment tabs */}
      <div className="border-b border-border flex items-center gap-1" data-testid="espm-env-tabs">
        <EnvTab env="test" icon={FlaskConical} label="Sandbox (Test)" />
        <EnvTab env="prod" icon={Rocket} label="Production" />
      </div>

      {/* First-visit get-started hint */}
      {firstTime && (
        <div className="flex items-start gap-2 border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-none" data-testid="espm-getstarted-hint">
          <FlaskConical className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <b>Get started by connecting to the Sandbox (Test) instance.</b> Enter your ENERGY STAR test credentials in the Connection panel below and click <b>Test</b>. When you’re ready, let me know and we’ll connect Production.
          </div>
        </div>
      )}

      {/* Sandbox limits banner */}
      {isTest && (
        <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-none" data-testid="espm-sandbox-limit-banner">
          <FlaskConical className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <b>Sandbox mode</b> — safe for testing. Limited to <b>{limits.max_properties} properties</b> and <b>{limits.max_meters} meters</b>.
            <span className="text-muted-foreground"> Currently using {counts.properties}/{limits.max_properties} properties · {counts.meters}/{limits.max_meters} meters.</span>
            <span className="block text-muted-foreground mt-0.5">Once your test looks right, set up the <b>Production</b> tab for unlimited, scheduled pushing.</span>
          </div>
        </div>
      )}

      {/* KPI strip — top & full width so the load status is never lost at the bottom */}
      <div className="border border-border rounded-none p-5 bg-card" data-testid="espm-kpi-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2 text-sm"><Gauge className="h-4 w-4" /> Load KPI <span className="text-xs font-normal text-muted-foreground">({isTest ? 'Sandbox' : 'Production'})</span></h3>
          <Tip label="Recalculate the load KPI from the latest bill data & push status">
            <Button variant="ghost" size="icon" className="rounded-none h-7 w-7" onClick={loadKpi} data-testid="espm-kpi-refresh"><RefreshCw className="h-4 w-4" /></Button>
          </Tip>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div data-testid="espm-kpi-active-meters">
            <div className="text-2xl font-bold">{kpi?.active_meters ?? 0}<span className="text-xs text-muted-foreground font-normal"> / {kpi?.mapped_meters ?? 0}</span></div>
            <div className="text-[11px] text-muted-foreground">Active meters</div>
          </div>
          <div data-testid="espm-kpi-completeness">
            <div className={`text-2xl font-bold ${kpiColor}`}>{kpi?.completeness_pct ?? 0}%</div>
            <div className="text-[11px] text-muted-foreground">Months loaded</div>
          </div>
          <div data-testid="espm-kpi-loaded">
            <div className="text-2xl font-bold">{fmt(kpi?.loaded_month_reads)}<span className="text-xs text-muted-foreground font-normal"> / {fmt(kpi?.expected_month_reads)}</span></div>
            <div className="text-[11px] text-muted-foreground">Month-reads</div>
          </div>
          <div data-testid="espm-kpi-missed">
            <div className={`text-2xl font-bold ${(kpi?.missed_month_reads || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(kpi?.missed_month_reads)}</div>
            <div className="text-[11px] text-muted-foreground">Missed</div>
          </div>
        </div>
        <div className="h-2 w-full bg-muted rounded-none overflow-hidden mt-3">
          <div className={`h-full ${kpiBar}`} style={{ width: `${Math.min(100, kpi?.completeness_pct || 0)}%` }} />
        </div>
      </div>

      {/* Two-column: property list (main, wider) + setup sidebar */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 items-start">
        {/* MAIN — Property readiness + mapping */}
        <div className="xl:col-span-3 space-y-4">
          <div className="border border-border rounded-none p-6 bg-card space-y-4" data-testid="espm-mapping-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-bold flex items-center gap-2"><Building2 className="h-4 w-4" /> Properties <span className="text-xs font-normal text-muted-foreground">({isTest ? 'Sandbox' : 'Production'})</span></h3>
              <div className="flex items-center gap-2">
                <Tip label="Create every Ready property & meter in ESPM automatically and push the last 12 months — matches existing ones by name, never makes duplicates">
                  <Button size="sm" className="rounded-none" disabled={!connected || provisioning || !readyToConnect.length} onClick={() => autoCreate(readyToConnect, null, { push: true })} data-testid="espm-autocreate-btn">
                    {provisioning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />} Connect all Ready{readyToConnect.length ? ` (${readyToConnect.length})` : ''}
                  </Button>
                </Tip>
                <Tip label="Reload the property list and their current ESPM mappings">
                  <Button variant="ghost" size="sm" className="rounded-none" onClick={loadMappings} data-testid="espm-refresh-mappings"><RefreshCw className={`h-4 w-4 ${loadingMap ? 'animate-spin' : ''}`} /></Button>
                </Tip>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Each property shows whether it is ready for ENERGY STAR. Fix anything flagged, then <b>Connect &amp; sync</b> to create it in ESPM and push its reads. Mappings are per-environment.</p>

            {/* Readiness filter pills */}
            <ReadinessFilters counts={readinessCounts} active={readinessFilter} onChange={setReadinessFilter} testidPrefix="espm" />

            {loadingMap ? (
              <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : !allProps.length ? (
              <p className="text-sm text-muted-foreground p-6 text-center border border-dashed border-border" data-testid="espm-no-properties">
                No properties are ready for ENERGY STAR setup. Add the gross floor area, primary function and at least one supported meter to continue.
              </p>
            ) : !visibleProps.length ? (
              <p className="text-sm text-muted-foreground p-6 text-center border border-dashed border-border" data-testid="espm-no-properties-filtered">
                {readinessFilter === 'ready'
                  ? 'No properties are ready for ENERGY STAR setup. Add the gross floor area, primary function and at least one supported meter to continue.'
                  : 'No properties in this view.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="espm-mapping-table">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted-foreground">
                      <th className="py-2 pr-4">Property</th>
                      {COMMODITIES.map((c) => <th key={c.key} className="py-2 px-2">{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProps.map((p) => {
                      const r = p.readiness || {};
                      const isReady = r.status === 'ready';
                      const isConn = r.status === 'connected';
                      return (
                      <tr key={p.site_uid} className="border-b border-border/50 align-top" data-testid={`espm-prop-row-${p.site_uid}`}>
                        <td className="py-3 pr-4 min-w-[240px]">
                          <div className="font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{[p.city, p.state].filter(Boolean).join(', ')}</div>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <ReadinessBadge status={r.status} />
                            <ScoreEligibilityBadge value={r.score_eligibility} />
                          </div>
                          {r.gfa ? (
                            <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1"><Ruler className="h-3 w-3" /> {fmt(r.gfa)} {r.gfa_units || 'sq ft'} · {r.primary_function}</div>
                          ) : null}
                          {(r.reasons || []).length > 0 && (
                            <div className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-500" data-testid={`espm-reasons-${p.site_uid}`}>
                              <span className="font-semibold">Add: </span>{r.reasons.join(' · ')}
                            </div>
                          )}
                          {isReady && (
                            <Tip label="Create this property + meters in ESPM and push its last 12 months of reads">
                              <Button size="sm" className="rounded-none mt-2 h-7 text-xs" disabled={!connected || rowBusy === p.site_uid} onClick={() => autoCreate([p.site_uid], null, { push: true })} data-testid={`espm-connect-${p.site_uid}`}>
                                {rowBusy === p.site_uid ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PlugZap className="h-3.5 w-3.5 mr-1.5" />} Connect &amp; sync
                              </Button>
                            </Tip>
                          )}
                          {isConn && p.espm_property_id ? (
                            <div className="mt-2" data-testid={`espm-rep-${p.site_uid}`}>
                              <Tip label="How these meters represent the property in ENERGY STAR. 'Common Area only' scopes metrics to shared/house loads (e.g. multifamily common areas) rather than the whole building.">
                                <Select
                                  value={p.representation_key || 'whole'}
                                  onValueChange={(v) => setRepresentation(p.site_uid, v === 'common' ? 'Common Area Energy Consumption Only' : 'Whole Property')}
                                >
                                  <SelectTrigger className="h-7 rounded-none text-[11px] w-[172px]" data-testid={`espm-rep-select-${p.site_uid}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="whole">Whole Property</SelectItem>
                                    <SelectItem value="common">Common Area only</SelectItem>
                                  </SelectContent>
                                </Select>
                              </Tip>
                            </div>
                          ) : null}
                        </td>
                        {COMMODITIES.map((c) => {
                          const usage = p.usage?.[`${c.key === 'electricity' ? 'electricity_kwh' : c.key === 'gas' ? 'gas_therms' : 'water_gallons'}`] || 0;
                          const mapped = p.mappings?.[c.key];
                          const Icon = c.icon;
                          if (usage <= 0 && !mapped) return <td key={c.key} className="py-3 px-2 text-xs text-muted-foreground/50">—</td>;
                          return (
                            <td key={c.key} className="py-3 px-2">
                              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1"><Icon className="h-3 w-3" /> {fmt(usage)} {c.unit}</div>
                              {mapped ? (
                                <div className="flex items-center gap-1">
                                  <Badge variant="outline" className="rounded-none text-[10px] max-w-[140px] truncate" title={mapped.espm_meter_name}>#{mapped.espm_meter_id}{mapped.espm_meter_name ? ` · ${mapped.espm_meter_name}` : ''}</Badge>
                                  <Tip label={`View & change which ESPM meter this ${c.label.toLowerCase()} usage is linked to`}>
                                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-none" onClick={() => openMapDialog(p, c.key)} data-testid={`espm-remap-${p.site_uid}-${c.key}`}><Link2 className="h-3 w-3" /></Button>
                                  </Tip>
                                  <Tip label="Remove this ESPM meter mapping (does not delete the meter in ESPM)">
                                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-none text-red-600" onClick={() => removeMapping(mapped.id)} data-testid={`espm-unmap-${p.site_uid}-${c.key}`}><Trash2 className="h-3 w-3" /></Button>
                                  </Tip>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <Tip label={`Link this ${c.label.toLowerCase()} usage to an existing ESPM meter`}>
                                    <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" disabled={!connected} onClick={() => openMapDialog(p, c.key)} data-testid={`espm-map-${p.site_uid}-${c.key}`}>Map meter</Button>
                                  </Tip>
                                  <Tip label={`Create this property + a ${c.label.toLowerCase()} meter in ESPM and push its last 12 months (no duplicates)`}>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-none" disabled={!connected || rowBusy === p.site_uid} onClick={() => autoCreate([p.site_uid], [c.key], { push: true })} data-testid={`espm-create-${p.site_uid}-${c.key}`}>
                                      {rowBusy === p.site_uid ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
                                    </Button>
                                  </Tip>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* SIDEBAR — accordions: Connection / Push / Fallbacks / History */}
        <div className="xl:col-span-1 xl:sticky xl:top-4 self-start" data-testid="espm-sidebar">
          <Accordion type="multiple" value={sidebarOpen} onValueChange={setSidebarOpen} className="space-y-4">
          {/* Connection */}
          <AccordionItem value="connection" className="border border-border rounded-none bg-card" data-testid="espm-connection-card">
            <AccordionTrigger className="px-5 py-3 hover:no-underline" data-testid="espm-acc-connection">
              <span className="font-bold flex items-center gap-2 text-sm flex-wrap">
                <Plug className="h-4 w-4" /> {isTest ? 'Sandbox' : 'Production'} Connection
                {connected ? (
                  <Badge className="rounded-none bg-green-600 hover:bg-green-600" data-testid="espm-status-connected"><CheckCircle2 className="h-3 w-3 mr-1" /> Connected</Badge>
                ) : (
                  <Badge variant="outline" className="rounded-none" data-testid="espm-status-disconnected"><AlertTriangle className="h-3 w-3 mr-1" /> Not connected</Badge>
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 space-y-4">
              <p className="text-[11px] text-muted-foreground" data-testid="espm-env-hint">
                {isTest ? 'Sandbox and Production are separate ESPM accounts with separate logins.' : 'Enter your real Portfolio Manager credentials here.'}{' '}
                Log in or create your {isTest ? 'Sandbox' : 'Production'} account at{' '}
                <a href={isTest ? 'https://portfoliomanager.energystar.gov/pmtest' : 'https://portfoliomanager.energystar.gov/pm'}
                  target="_blank" rel="noopener noreferrer" className="underline text-amber-600 hover:text-amber-700" data-testid="espm-portal-link">
                  portfoliomanager.energystar.gov/{isTest ? 'pmtest' : 'pm'}
                </a>.
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">ESPM Username</Label>
                  <Input className="rounded-none mt-1" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="portfolio-manager-username" data-testid="espm-username-input" />
                </div>
                <div>
                  <Label className="text-xs">ESPM Password</Label>
                  <Input type="password" className="rounded-none mt-1" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder={envSlot.password_set ? '•••••••• (unchanged)' : 'password'} data-testid="espm-password-input" />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.auto_push} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_push: v }))} data-testid="espm-autopush-switch" />
                  <Label className="text-xs">
                    {isTest ? 'Auto-push (Sandbox)' : 'Go live: daily auto-push to Production'}
                  </Label>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <Tip label={`Save these ${isTest ? 'Sandbox' : 'Production'} credentials`}>
                  <Button size="sm" className="rounded-none" onClick={saveConfig} disabled={saving} data-testid="espm-save-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save
                  </Button>
                </Tip>
                <Tip label="Check these credentials can sign in to ESPM (saves first if you changed them)">
                  <Button variant="outline" size="sm" className="rounded-none" onClick={testConnection} disabled={testing || !form.username || (!envSlot.password_set && !form.password)} data-testid="espm-test-btn">
                    {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plug className="h-4 w-4 mr-1.5" />} Test
                  </Button>
                </Tip>
              </div>
              {envSlot.last_test_status === 'failure' && (
                <span className="text-xs text-red-600 block" data-testid="espm-test-error">{envSlot.last_test_message}</span>
              )}
              {connected && envSlot.espm_account_id && (
                <span className="text-[11px] text-muted-foreground">ESPM account #{envSlot.espm_account_id}</span>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Push meter reads */}
          <AccordionItem value="push" className="border border-border rounded-none bg-card" data-testid="espm-push-card">
            <AccordionTrigger className="px-5 py-3 hover:no-underline" data-testid="espm-acc-push">
              <span className="font-bold flex items-center gap-2 text-sm"><Send className="h-4 w-4" /> Push meter reads</span>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 space-y-3">
              <p className="text-[11px] text-muted-foreground">Sends monthly consumption for all mapped meters. Already-pushed months are skipped (enable re-push to override). {mappingCount} meter(s) mapped.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">From</Label>
                  <Input type="date" className="rounded-none mt-1 h-9 text-xs" value={pushRange.date_from} onChange={(e) => setPushRange((r) => ({ ...r, date_from: e.target.value }))} data-testid="espm-push-from" />
                </div>
                <div>
                  <Label className="text-[11px]">To</Label>
                  <Input type="date" className="rounded-none mt-1 h-9 text-xs" value={pushRange.date_to} onChange={(e) => setPushRange((r) => ({ ...r, date_to: e.target.value }))} data-testid="espm-push-to" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={pushRange.estimated} onCheckedChange={(v) => setPushRange((r) => ({ ...r, estimated: v }))} data-testid="espm-push-estimated" />
                <Label className="text-xs">Mark as estimated</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={pushRange.force} onCheckedChange={(v) => setPushRange((r) => ({ ...r, force: v }))} data-testid="espm-push-force" />
                <Label className="text-xs">Re-push existing months</Label>
              </div>
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <Tip label="See exactly which months & values would be sent — nothing is transmitted to ESPM">
                  <Button variant="outline" size="sm" className="rounded-none" onClick={previewPush} disabled={previewing || mappingCount === 0} data-testid="espm-preview-btn">
                    {previewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />} Preview
                  </Button>
                </Tip>
                <Tip label={`Send monthly meter reads to ESPM ${isTest ? 'Sandbox' : 'Production'} now (already-pushed months are skipped)`}>
                  <Button size="sm" className="rounded-none" onClick={pushNow} disabled={pushing || !connected || mappingCount === 0} data-testid="espm-push-btn">
                    {pushing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Push now
                  </Button>
                </Tip>
              </div>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid="espm-preview-hint">
                <ShieldCheck className="h-3 w-3 text-green-600" /> <b>Preview</b> shows what would be sent without sending — safe for demos.
              </p>
            </AccordionContent>
          </AccordionItem>

          {/* Property creation fallbacks */}
          <AccordionItem value="fallbacks" className="border border-border rounded-none bg-card" data-testid="espm-defaults-card">
            <AccordionTrigger className="px-5 py-3 hover:no-underline" data-testid="espm-acc-fallbacks">
              <span className="font-bold flex items-center gap-2 text-sm"><Wand2 className="h-4 w-4" /> Property creation fallbacks</span>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 space-y-3">
              <p className="text-[11px] text-muted-foreground">Used <b>only</b> to fill blanks when auto-creating a property in ESPM. Each property still uses its own function &amp; year when set. <b>Floor area is never assumed</b> — properties without a floor area are flagged as “Missing property details”.</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Fallback primary function</Label>
                  <Select value={propDefaults.primary_function} onValueChange={(v) => setPropDefaults((d) => ({ ...d, primary_function: v }))}>
                    <SelectTrigger className="rounded-none mt-1 h-9" data-testid="espm-default-pf"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(pfList.length ? pfList : ['Other']).map((pf) => <SelectItem key={pf} value={pf}>{pf}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Fallback year built</Label>
                  <Input type="number" className="rounded-none mt-1 h-9" value={propDefaults.year_built} onChange={(e) => setPropDefaults((d) => ({ ...d, year_built: e.target.value }))} data-testid="espm-default-year" />
                </div>
                <Button size="sm" variant="outline" className="rounded-none" onClick={saveConfig} disabled={saving} data-testid="espm-defaults-save">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save fallbacks
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* History */}
          {logs.length > 0 && (
            <AccordionItem value="history" className="border border-border rounded-none bg-card" data-testid="espm-history-card">
              <AccordionTrigger className="px-5 py-3 hover:no-underline" data-testid="espm-acc-history">
                <span className="font-bold flex items-center gap-2 text-sm"><History className="h-4 w-4" /> Recent pushes</span>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5 space-y-2">
                {logs.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-xs border-b border-border/50 pb-1.5" data-testid={`espm-log-${i}`}>
                    <div className="flex items-center gap-1.5">
                      {l.success ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                      <span>{new Date(l.ts).toLocaleDateString()}</span>
                      <Badge variant="outline" className="rounded-none text-[9px]">{l.source}</Badge>
                    </div>
                    <span className="text-muted-foreground">{l.records_pushed} rec · {l.meters} m</span>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          )}
          </Accordion>
        </div>
      </div>

      {/* Mapping dialog */}
      <Dialog open={!!mapDlg} onOpenChange={(o) => !o && setMapDlg(null)}>
        <DialogContent className="rounded-none" data-testid="espm-map-dialog">
          <DialogHeader>
            <DialogTitle>Link ESPM meter</DialogTitle>
            <DialogDescription>Link this property's commodity to an ENERGY STAR meter in the {isTest ? 'Sandbox' : 'Production'} account.</DialogDescription>
          </DialogHeader>
          {mapDlg && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {mapDlg.property_name} · <span className="capitalize font-medium">{mapDlg.commodity}</span>
              </p>

              {/* Currently linked meters at this property */}
              {mapDlg.mappings && Object.values(mapDlg.mappings).some(Boolean) && (
                <div className="border border-border rounded-none p-3 bg-muted/30" data-testid="espm-dlg-linked">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Already linked at this property</div>
                  <div className="space-y-1">
                    {COMMODITIES.map((c) => {
                      const mm = mapDlg.mappings?.[c.key];
                      if (!mm) return null;
                      const Icon = c.icon;
                      return (
                        <div key={c.key} className="flex items-center gap-1.5 text-xs" data-testid={`espm-dlg-linked-${c.key}`}>
                          <Icon className="h-3 w-3 text-muted-foreground" /> {c.label}:
                          <Badge variant="outline" className="rounded-none text-[10px]">#{mm.espm_meter_id}{mm.espm_meter_name ? ` · ${mm.espm_meter_name}` : ''}</Badge>
                          {c.key === mapDlg.commodity && <span className="text-[10px] text-amber-600">(this one)</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">ESPM Property</Label>
                <div className="rounded-none mt-1 border border-border bg-muted/40 px-3 py-2 text-sm" data-testid="espm-dlg-property-fixed">
                  {mapDlg.property_name}
                  {mapDlg.espm_property_id ? <span className="text-muted-foreground"> · #{mapDlg.espm_property_id}</span> : null}
                </div>
              </div>
              {!mapDlg.espm_property_id ? (
                <p className="text-[12px] text-amber-700 dark:text-amber-500 border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-none" data-testid="espm-dlg-notcreated">
                  This property isn’t in ENERGY STAR yet. Use <b>Connect &amp; sync</b> (or the <b>+</b> on the row) to create it first, then come back here to link its meters.
                </p>
              ) : (
                <div>
                  <Label className="text-xs">ESPM Meter to link</Label>
                  <Select value={selMeter} onValueChange={setSelMeter}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="espm-dlg-meter"><SelectValue placeholder={dlgBusy ? 'Loading…' : 'Select a meter'} /></SelectTrigger>
                    <SelectContent>
                      {(espmMeters || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name || `Meter #${m.id}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {espmMeters && !espmMeters.length && (
                    <p className="text-[11px] text-muted-foreground mt-1">This property has no meters yet — use the “+” on the row to auto-create one.</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setMapDlg(null)}>Cancel</Button>
            <Tip label="Save this property → ESPM meter link so consumption pushes to it">
              <Button className="rounded-none" onClick={saveMapping} disabled={dlgBusy || !selProp || !selMeter} data-testid="espm-dlg-save">Link meter</Button>
            </Tip>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview (dry run) dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="espm-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5" /> Push preview (dry run)</DialogTitle>
            <DialogDescription>A safe preview of the monthly consumption that would be sent to ENERGY STAR. Nothing is transmitted.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 border border-green-600/40 bg-green-50 dark:bg-green-950/30 p-3 rounded-none" data-testid="espm-preview-safe-banner">
                <ShieldCheck className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <b>{fmt(preview.records_pushed)} record(s)</b> would be pushed across <b>{preview.meters}</b> meter(s)
                  {preview.date_from ? <> for <b>{preview.date_from}</b> → <b>{preview.date_to}</b></> : null}.
                  <div className="text-xs text-muted-foreground mt-1">Nothing was sent to ESPM — this is a preview only.</div>
                </div>
              </div>
              <div className="space-y-3">
                {(preview.results || []).filter((r) => (r.records || 0) > 0).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center p-4" data-testid="espm-preview-empty">
                    No new months to push in this range (already-pushed months are skipped — enable "Re-push existing months" to include them).
                  </p>
                ) : (
                  (preview.results || []).filter((r) => (r.records || 0) > 0).map((r, i) => (
                    <div key={i} className="border border-border rounded-none p-3" data-testid={`espm-preview-row-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-sm">{r.property_name || r.site_uid}</div>
                        <Badge variant="outline" className="rounded-none capitalize">{r.commodity} · {r.records} month(s)</Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(r.preview || []).slice(0, 24).map((p, j) => (
                          <Badge key={j} variant="outline" className="rounded-none text-[10px] font-normal">
                            {p.month}: {fmt(p.usage)}
                          </Badge>
                        ))}
                        {(r.preview || []).length > 24 && (
                          <span className="text-[10px] text-muted-foreground">+{(r.preview || []).length - 24} more</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setPreviewOpen(false)} data-testid="espm-preview-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAQ drawer */}
      <Sheet open={faqOpen} onOpenChange={setFaqOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="espm-faq-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><HelpCircle className="h-5 w-5 text-amber-500" /> Energy Star setup — FAQ</SheetTitle>
            <SheetDescription>Everything you need to connect Sandbox &amp; Production and push meter reads.</SheetDescription>
          </SheetHeader>
          <Accordion type="single" collapsible className="mt-4">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`espm-faq-item-${i}`}>
                <AccordionTrigger className="text-left text-sm">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </SheetContent>
      </Sheet>
    </div>
    </TooltipProvider>
  );
}
